import React, { useState, useEffect, useCallback } from "react";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Title from "./Title";
import CsvDownloadButton from "react-json-to-csv";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import DashboardTable from "./DashboardTable";
import AddCompany from "./AddCompany";
import EditCompany from "./EditCompany";
import { fetchCompanies } from "../../apis/CompanyAPI";
import axios from "axios";
import Switch from "@mui/material/Switch";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";

/**
 * Gets the refresh token from cookies
 *
 * @returns {string} The refresh token
 */
const getRefreshToken = () => {
  const cookies = document.cookie.split(";");
  let token = null;

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "_auth_refresh") {
      token = decodeURIComponent(value);
      break;
    }
  }

  return token;
};

/**
 * Companies component for displaying and managing company data
 *
 * @component
 * @returns {JSX.Element} The rendered Companies component
 */
function Companies() {
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Function to refresh token
  const refreshToken = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        // Redirect to login if no refresh token
        window.location.href = "/login";
        return;
      }

      // Call the refresh token endpoint but we don't need to store the response
      // since we're redirecting to login anyway
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/refresh-token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        }
      );

      // Update tokens in cookies (should be handled by your auth system)
      // For now, just redirect to login to get fresh tokens
      window.location.href = "/login";
    } catch (refreshError) {
      console.error("Error refreshing token:", refreshError);
      // If refresh fails, redirect to login
      window.location.href = "/login";
    }
  }, []);

  // Function to fetch companies
  const fetchCompaniesData = useCallback(async () => {
    try {
      setError(null);
      const companiesData = await fetchCompanies();
      setCompanies(
        companiesData.map((company) => ({
          ...company,
          create_date: new Date(company.create_date).toLocaleDateString(),
        }))
      );
    } catch (error) {
      console.error("Error fetching Companies:", error);

      // Handle 401 Unauthorized or 422 Unprocessable Entity errors (token expired or invalid)
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 422)
      ) {
        // Try to refresh the token
        await refreshToken();
      } else {
        setError("Failed to load companies. Please try again later.");
      }
    }
  }, [refreshToken]);

  useEffect(() => {
    fetchCompaniesData();
  }, [fetchCompaniesData]);

  const columns = [
    "Company ID",
    "Company Name",
    "Description",
    "Meeting Credits",
    "Recurring Credits",
    "Create Date",
    "Registration Code",
    "Status"
  ];

  const columnMapping = {
    "Company ID": "tid",
    "Company Name": "name",
    Description: "description",
    "Meeting Credits": "meeting_cred",
    "Recurring Credits": "recurring_credits",
    "Create Date": "create_date",
    "Registration Code": "reg_code",
    "Status": "is_active"
  };

  const formatValues = {
    "Status": (value) => (value === 1 || value === true) ? "Active" : "Inactive"
  };

  const handleCompanyAdded = async () => {
    // After a company is added, refetch the list of companies
    try {
      await fetchCompaniesData();
    } catch (error) {
      console.error("Error fetching Companies after addition:", error);
      // Error handling is already in fetchCompaniesData
    }
  };

  const filteredCompanies = showInactive 
    ? companies 
    : companies.filter(c => c.is_active !== 0 && c.is_active !== false);

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
      >
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <React.Fragment>
            <Title>
              Companies
              <FilterListIcon 
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  color: "black",
                  background: "none",
                  border: "none",
                  float: "right",
                  margin: "0px 0px 0px 10px",
                  cursor: "pointer"
                }}
              />
              <CsvDownloadButton
                data={filteredCompanies}
                filename={"Company List.csv"}
                style={{
                  background: "none",
                  border: "none",
                  float: "right",
                  cursor: "pointer"
                }}
              >
                <FileDownloadOutlinedIcon />
              </CsvDownloadButton>
              <AddCompany onCompanyAdded={handleCompanyAdded} />
              <EditCompany
                data={companies}
                onCompanyEditted={handleCompanyAdded}
              />
            </Title>
            
            <Collapse in={showFilters}>
              <Box sx={{ 
                p: 2.5, 
                mb: 2, 
                backgroundColor: '#fafafa', 
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    name="showInactive"
                    color="primary"
                  />
                  <Box sx={{ fontSize: '0.95rem', color: '#424242' }}>
                    Show Inactive Companies
                  </Box>
                </Box>
              </Box>
            </Collapse>
            
            {error ? (
              <div
                style={{ color: "red", textAlign: "center", padding: "20px" }}
              >
                {error}
              </div>
            ) : (
              <DashboardTable
                data={filteredCompanies}
                columns={columns}
                columnMapping={columnMapping}
                refreshData={fetchCompaniesData}
                formatValues={formatValues}
              />
            )}
          </React.Fragment>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default Companies;
